/**
 * Resource pages content (Skills framework, Interaction gallery, Report gallery,
 * Guides and glossary, Comparisons, Client stories). Source: Resources mockup.
 * Keys are the route slugs: /resources/<key>. Edit text here, not in the JSX.
 *
 * The tile grid is a PLACEHOLDER layout (per the mockup). When real content is ready,
 * add an `items` array to a resource and ResourcePage will render those instead of placeholders.
 */

import skillsHero from '../../assets/images/eb/resources/skills-framework.png';
import interactionGallery from '../../assets/images/eb/resources/interaction-gallery.png';
import reportGallery from '../../assets/images/eb/resources/report-gallery.png';
import guidesGlossary from '../../assets/images/eb/resources/guide-glossary.png';


export const RESOURCES = {
  'skills-framework': {
    name: 'Skills framework', heroImage: skillsHero, icon: 'target', short: 'Roles, skills and proficiency levels',
    intro: 'See how roles, skills, and proficiency levels connect, and how they feed the workforce readiness score.',
    heroNote: 'Diagram of a role profile: skills grouped as core, compliance and professional, levels L1 to L4.',
    listTitle: 'Browse skill domains',
    filters: ['All domains', 'Core', 'Compliance', 'Professional'],
    thumbLabel: 'DOMAIN ICON', thumbH: 120, tag: 'SKILL DOMAIN', count: 6,
    items: [
      { cat: 'Core', icon: 'pencil', title: 'Communication and collaboration',
        desc: 'Everyday skills every role needs, from L1 basics to L4 coaching others.',
        skills: ['Clear writing', 'Team coordination', 'Customer conversations'] },
      { cat: 'Core', icon: 'layout', title: 'Digital and data fluency',
        desc: 'Working confidently in company systems and reading the numbers.',
        skills: ['Company systems', 'Reading dashboards', 'Data accuracy'] },
      { cat: 'Core', icon: 'users', title: 'Leadership and people management',
        desc: 'Skills for managers and supervisors, from L1 to L4.',
        skills: ['Coaching', 'Feedback', 'Running a team'] },
      { cat: 'Compliance', icon: 'shield', title: 'Health, safety and environment',
        desc: 'Mandatory skills, tracked with validity dates.',
        skills: ['Permit to work', 'Emergency response', 'Incident reporting'] },
      { cat: 'Compliance', icon: 'check', title: 'Regulatory and quality',
        desc: 'Evidence-backed skills that auditors check.',
        skills: ['GxP', 'AML and conduct', 'Audit readiness'] },
      { cat: 'Professional', icon: 'target', title: 'Role-specific technical skills',
        desc: 'Skills defined per role and industry.',
        skills: ['Equipment operation', 'Product knowledge', 'Process execution'] },
    ],
  },
  'interaction-gallery': {
    name: 'Interaction gallery', heroImage: interactionGallery, icon: 'eye', short: 'Real learner interactions',
    intro: 'Browse real learner interactions, from branching scenarios to assessments, and see what engaging digital learning looks like.',
    heroNote: 'Looping preview of a branching scenario interaction.',
    listTitle: 'Interactions',
    filters: ['All', 'Scenarios', 'Assessments', 'Gamified', 'Immersive'],
    thumbLabel: 'INTERACTION PREVIEW', thumbH: 140, tag: 'INTERACTION TYPE', count: 9,
    items: [
      { cat: 'Scenarios', icon: 'users', title: 'Customer conversation branching',
        desc: 'Learners pick responses and see how the customer reacts.',
        skills: ['Active listening', 'De-escalation', 'Service recovery'] },
      { cat: 'Scenarios', icon: 'shield', title: 'Safety incident response',
        desc: 'Choose the first action during an incident and see the outcome.',
        skills: ['Emergency response', 'Incident reporting', 'Judgment'] },
      { cat: 'Scenarios', icon: 'target', title: 'Manager coaching conversation',
        desc: 'Practice giving feedback to a team member in a branching dialogue.',
        skills: ['Coaching', 'Feedback', 'Empathy'] },
      { cat: 'Assessments', icon: 'check', title: 'Knowledge check',
        desc: 'Short question sets with instant feedback and scoring.',
        skills: ['Recall', 'Policy knowledge', 'Compliance'] },
      { cat: 'Assessments', icon: 'pencil', title: 'Applied skills assessment',
        desc: 'Scenario-based questions that test skills in context.',
        skills: ['Application', 'Decision making', 'Evidence'] },
      { cat: 'Gamified', icon: 'award', title: 'Team leaderboard challenge',
        desc: 'Points, badges and rankings that keep learners engaged.',
        skills: ['Motivation', 'Streaks', 'Badges'] },
      { cat: 'Gamified', icon: 'bolt', title: 'Timed quiz sprint',
        desc: 'Fast rounds that reinforce key facts through repetition.',
        skills: ['Speed', 'Retention', 'Revision'] },
      { cat: 'Immersive', icon: 'eye', title: '360 degree site walkthrough',
        desc: 'Explore a workplace and spot hazards in context.',
        skills: ['Observation', 'Hazard spotting', 'Awareness'] },
      { cat: 'Immersive', icon: 'layout', title: 'Simulated system practice',
        desc: 'Practice real workflows in a safe copy of company systems.',
        skills: ['Company systems', 'Process execution', 'Accuracy'] },
    ],
  },
  'report-gallery': {
    name: 'Report gallery', heroImage: reportGallery, icon: 'layout', short: 'Dashboards leaders use every day',
    intro: 'Preview the dashboards and reports that learning leaders, managers, and executives use every day.',
    heroNote: 'Screenshot of the readiness dashboard by team, with illustrative data.',
    listTitle: 'Reports',
    filters: ['All', 'Readiness', 'Compliance', 'Skills', 'Engagement'],
    thumbLabel: 'REPORT SCREENSHOT', thumbH: 140, tag: 'REPORT CATEGORY', count: 9,
    items: [
      { cat: 'Readiness', icon: 'target', title: 'Workforce readiness by team',
        desc: 'Readiness score for each team, with the biggest gaps highlighted.',
        skills: ['Readiness score', 'Team comparison', 'Gap analysis'] },
      { cat: 'Readiness', icon: 'users', title: 'Role readiness overview',
        desc: 'How many people are ready, nearly ready or not ready for their role.',
        skills: ['Role profiles', 'Proficiency levels', 'Ready now'] },
      { cat: 'Readiness', icon: 'bolt', title: 'Executive summary',
        desc: 'One page view of readiness trends for leadership reviews.',
        skills: ['Trends', 'Risk areas', 'Quarter on quarter'] },
      { cat: 'Compliance', icon: 'shield', title: 'Mandatory training status',
        desc: 'Completion and overdue counts for required courses.',
        skills: ['Overdue learners', 'Due dates', 'Completion rate'] },
      { cat: 'Compliance', icon: 'check', title: 'Certification expiry tracker',
        desc: 'Upcoming expiries by person, team and site.',
        skills: ['Validity dates', 'Renewals', 'Audit evidence'] },
      { cat: 'Skills', icon: 'pencil', title: 'Skills gap report',
        desc: 'Where current proficiency falls short of the target level.',
        skills: ['Target levels', 'Skill gaps', 'Training needs'] },
      { cat: 'Skills', icon: 'layout', title: 'Skills coverage matrix',
        desc: 'Who holds which skills, across teams and locations.',
        skills: ['Coverage', 'Single points of failure', 'Succession'] },
      { cat: 'Engagement', icon: 'eye', title: 'Learner activity report',
        desc: 'Logins, time spent and course progress over time.',
        skills: ['Active learners', 'Time spent', 'Progress'] },
      { cat: 'Engagement', icon: 'award', title: 'Course feedback summary',
        desc: 'Ratings and comments collected after each course.',
        skills: ['Ratings', 'Comments', 'Content quality'] },
    ],
  },
  guides: {
    name: 'Guides and glossary', heroImage: guidesGlossary, icon: 'book', short: 'Plain language answers',
    intro: 'Get plain language answers on workforce readiness, skills based learning, LMS and LXP terms, SCORM, and AI in learning.',
    heroNote: 'Featured guide cover image.',
    listTitle: 'Guides and terms',
    filters: ['Guides', 'Glossary A to Z', 'SCORM', 'AI in learning'],
    thumbLabel: 'GUIDE COVER', thumbH: 120, tag: 'GUIDE', count: 6,
    items: [
      { cat: 'Guides', icon: 'target', title: 'What is workforce readiness?',
        desc: 'A plain language introduction to measuring whether people can do their jobs.',
        skills: ['Readiness score', 'Role profiles', 'Proficiency levels'] },
      { cat: 'Guides', icon: 'users', title: 'A guide to skills based learning',
        desc: 'How to move from course completion to skills and evidence.',
        skills: ['Skills framework', 'Assessments', 'Evidence'] },
      { cat: 'Glossary A to Z', icon: 'book', title: 'Learning terms from A to Z',
        desc: 'Short definitions for the terms you will meet when choosing a platform.',
        skills: ['LMS', 'LXP', 'Competency'] },
      { cat: 'Glossary A to Z', icon: 'pencil', title: 'Compliance and audit terms',
        desc: 'Validity dates, certifications and audit evidence explained simply.',
        skills: ['Validity', 'Certification', 'Audit trail'] },
      { cat: 'SCORM', icon: 'layout', title: 'SCORM explained',
        desc: 'What SCORM is, how it tracks learners and when to use it.',
        skills: ['SCORM 1.2', 'SCORM 2004', 'Tracking'] },
      { cat: 'AI in learning', icon: 'bolt', title: 'AI in learning, in plain language',
        desc: 'How AI helps build courses, personalize learning and brief managers.',
        skills: ['Course drafting', 'Learning buddy', 'Manager briefings'] },
    ],
  },
  comparisons: {
    name: 'Comparisons', icon: 'check', short: 'EthosBoard vs LMS, LXP, library',
    intro: 'Understand how EthosBoard differs from a traditional LMS, an LXP, and a content library, so you can choose with confidence.',
    heroNote: 'Side-by-side comparison graphic.',
    listTitle: 'Comparisons',
    filters: ['vs LMS', 'vs LXP', 'vs content library'],
    thumbLabel: 'COMPARISON GRAPHIC', thumbH: 140, tag: 'COMPARISON', count: 3,
  },
  'client-stories': {
    name: 'Client stories', icon: 'award', short: 'How teams prove readiness',
    intro: 'Read how organizations use digital learning to train people faster and prove readiness.',
    heroNote: 'Featured client photo and headline result, once approved.',
    listTitle: 'Stories',
    filters: ['All industries', 'Pharma', 'Aviation', 'Retail', 'BFSI'],
    thumbLabel: 'CLIENT PHOTO', thumbH: 200, tag: 'INDUSTRY · CLIENT', count: 6,
  },
};

export const RESOURCE_ORDER = Object.keys(RESOURCES);

export const RESOURCE_CTA = {
  title: 'See EthosBoard in action',
  text: 'Book a 30 minute walkthrough and see how EthosBoard can help you prove your workforce is ready.',
  button: 'Book a demo',
};

// Widths for the skeleton lines in placeholder tiles
export const SKELETON_WIDTHS = ['72%', '60%', '84%', '66%', '78%', '58%'];